import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from '../../components/Button';

interface LegalDocumentProps {
  content: string;
  embedded?: boolean;
}

interface TocItem {
  id: string;
  title: string;
  level: number;
}

const PLACEHOLDER_REPLACEMENTS: Record<string, string> = {
  '{{DATE_OF_LAUNCH}}': 'January 2026',
  '{{DATE\\_OF\\_LAUNCH}}': 'January 2026',
  '{{PRIVACY_POLICY_URL}}': '/privacy-policy',
  '{{PRIVACY\\_POLICY\\_URL}}': '/privacy-policy',
  '{{USE_POLICY_URL}}': '/acceptable-use-policy',
  '{{USE\\_POLICY\\_URL}}': '/acceptable-use-policy',
  '[link to privacy policy]': '/privacy-policy',
  '\\[link to privacy policy\\]': '/privacy-policy',
};

const processContent = (raw: string): string => {
  let processed = raw;
  
  Object.entries(PLACEHOLDER_REPLACEMENTS).forEach(([placeholder, value]) => {
    processed = processed.split(placeholder).join(value);
  });
  
  processed = processed.replace(/\\_/g, '_');
  processed = processed.replace(/\\#/g, '#');
  processed = processed.replace(/\\\[/g, '[');
  processed = processed.replace(/\\\]/g, ']');
  processed = processed.replace(/\\\+/g, '+');
  
  return processed;
};

const generateId = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
};

const extractToc = (content: string): TocItem[] => {
  const toc: TocItem[] = [];
  const lines = content.split('\n');
  
  lines.forEach((line) => {
    const h2Match = line.match(/^##\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    
    if (h2Match) {
      const title = h2Match[1].replace(/\*\*/g, '').trim();
      toc.push({ id: generateId(title), title, level: 2 });
    } else if (h3Match) {
      const title = h3Match[1].replace(/\*\*/g, '').trim();
      toc.push({ id: generateId(title), title, level: 3 });
    }
  });
  
  return toc;
};

const LegalDocument: React.FC<LegalDocumentProps> = ({ content, embedded = false }) => {
  const navigate = useNavigate();
  const [processedContent, setProcessedContent] = useState('');
  const [activeSection, setActiveSection] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setProcessedContent(processContent(content));
  }, [content]);

  const toc = useMemo(() => extractToc(processedContent), [processedContent]);

  useEffect(() => {
    if (toc.length > 0 && !activeSection) {
      const firstH2 = toc.find(item => item.level === 2);
      if (firstH2) setActiveSection(firstH2.id);
    }
  }, [toc, activeSection]);

  useEffect(() => {
    if (embedded || toc.length === 0) return;
    
    const handleScroll = () => {
      const headings = toc
        .filter(item => item.level === 2)
        .map(item => ({
          id: item.id,
          element: document.getElementById(item.id)
        }))
        .filter(h => h.element !== null);

      if (headings.length === 0) return;

      const scrollPosition = window.scrollY + 150;
      
      let currentSection = headings[0].id;
      
      for (const heading of headings) {
        if (heading.element && heading.element.offsetTop <= scrollPosition) {
          currentSection = heading.id;
        }
      }
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc, embedded]);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setActiveSection(id);
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const markdownContent = (
    <article className="legal-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
              {children}
            </h1>
          ),
          h2: ({ children }) => {
            const text = String(children).replace(/\*\*/g, '');
            const id = generateId(text);
            return (
              <h2 
                id={id}
                className="text-lg font-semibold text-gray-900 mt-8 mb-3 pt-6 border-t border-gray-100 scroll-mt-24 first:mt-0 first:border-t-0 first:pt-0"
              >
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            const text = String(children).replace(/\*\*/g, '');
            const id = generateId(text);
            return (
              <h3 
                id={id}
                className="text-base font-medium text-gray-800 mt-5 mb-2 scroll-mt-24"
              >
                {children}
              </h3>
            );
          },
          h4: ({ children }) => (
            <h4 className="text-sm font-medium text-gray-800 mt-4 mb-2">{children}</h4>
          ),
          p: ({ children }) => (
            <p className="text-sm text-gray-600 leading-relaxed mb-3">{children}</p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-800">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-600">{children}</em>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              className="text-[#07E5D1] hover:text-[#06D4C2] font-medium"
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),
          ul: ({ children }) => (
            <ul className="my-3 space-y-1.5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-3 space-y-1.5 list-decimal list-outside ml-5">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="text-sm text-gray-600 leading-relaxed pl-1 relative before:content-['•'] before:absolute before:-left-3 before:text-gray-400">
              {children}
            </li>
          ),
          hr: () => <hr className="border-gray-200 my-6" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-[#07E5D1] pl-4 my-4 text-gray-600 italic bg-[#E8FCFA]/30 py-2 rounded-r">
              {children}
            </blockquote>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </article>
  );

  if (embedded) {
    return <div ref={contentRef} className="legal-document-embedded">{markdownContent}</div>;
  }

  const h2Items = toc.filter(item => item.level === 2);

  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      <div className="max-w-6xl mx-auto">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-8">
          {h2Items.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-6 pt-10 pb-8">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  icon={<ArrowLeft className="w-4 h-4" />}
                  className="mb-6 text-gray-500 hover:text-gray-900"
                >
                  Back
                </Button>
                
                <nav>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
                    Contents
                  </p>
                  <div className="space-y-0.5">
                    {h2Items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left text-[13px] py-2 px-3 rounded-lg transition-all ${
                          activeSection === item.id
                            ? 'bg-[#E8FCFA] text-[#07E5D1] font-medium'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            </aside>
          )}

          <main className="px-4 sm:px-6 lg:px-0 py-10 lg:py-14">
            <div className="lg:hidden mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                icon={<ArrowLeft className="w-4 h-4" />}
                className="text-gray-500"
              >
                Back
              </Button>
            </div>

            <div ref={contentRef} className="max-w-2xl">
              {markdownContent}
            </div>

            <footer className="mt-12 pt-6 border-t border-gray-100 max-w-2xl">
              <p className="text-xs text-gray-400">
                © {new Date().getFullYear()} Bluepill, Inc. All rights reserved.
              </p>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LegalDocument;
